/**
 * <mango-finalize> — Finalize (FMV Approval): the full 3-column settlement REVIEW
 * + purchase decision, rebuilt to the prototype (view-finalize).
 * =====================================================================
 * The per-item FMV-assessment CSV is first uploaded on the SOW-completion (Equipment
 * Processing) screen, so by the time the provider reaches this screen the settlement
 * + breakdown already exist. This screen renders them READ-ONLY and offers the
 * purchase decision. Contract operations on this screen:
 *   • finalize    → FMV Approval → Mango Reviewing. PROVIDER-ADMIN ONLY (Staff get a
 *                   403 server-side). Stamps finalized_at — this IS the purchase.
 *   • fmv-decline → reprice (counter_price) or walk away instead of purchasing.
 *                   PROVIDER-ADMIN ONLY; opened from the decline/reprice modal.
 *   • fmv         → upload a REPLACEMENT per-item FMV CSV (multipart {file}); on
 *                   success we reload the work order so the breakdown/net settlement
 *                   refresh.
 *
 * THREE COLUMNS:
 *   • fin-left   — Job summary / Scope expansion / Documents verified (BOTH roles).
 *   • fin-center — FMV breakdown table + Net settlement (ADMIN ONLY): rendered only
 *                  when the Admin financial fields are present; for Staff the
 *                  serializer omits them and the whole column is hidden.
 *   • fin-right  — Finalization box (ADMIN ONLY) + Timeline (BOTH roles).
 *
 * ROLE-GATING IS COSMETIC (server enforces): when the serialized work order omits
 * `sow_rate` (a Staff response) the center column + Finalize box + payment are
 * hidden and the Admin-action gate is shown — gating on field presence, not a
 * client-side role flag.
 *
 * fmv_2 IS FORBIDDEN and appears NOWHERE in this file or its view. The Payment-
 * method dropdown is a COSMETIC MOCK (no backend).
 */
// finalizeWorkOrder / fmvDeclineWorkOrder / submitFmv are fired via the data-action
// registry (in client.ts), not called directly here — so they are not imported.
import { getWorkOrder, type WorkOrder, type FmvDeclineBody } from "../api/client";
import { applyBindings, wireActions } from "../bind";
import { loadView, hasFinancials, money, date, setText, toggle } from "./shared";

const VIEW_URL = new URL("../../views/finalize.html", import.meta.url).href;

export class MangoFinalize extends HTMLElement {
  static observedAttributes = ["data-short-name"];
  private disposeActions: (() => void) | null = null;
  private job: WorkOrder | null = null;

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    this.disposeActions = wireActions(this, {
      getBody: (ev) => {
        if (ev.action === "fmv-decline") {
          // reprice (counter_price) OR walk away — validated here; returns false
          // (with a status message) if the form is incomplete, aborting the call.
          return this.buildDeclineBody();
        }
        if (ev.action === "fmv") {
          // Replacement FMV CSV — multipart { file }; abort (with a message) if no
          // file is chosen. Mirrors the SOW screen's buildFileForm.
          const form = this.buildFmvForm();
          if (!form) {
            this.setUploadStatus("Choose an updated FMV CSV first.", "err");
            return false;
          }
          return form;
        }
        return undefined; // finalize takes no body
      },
      onSuccess: (ev) => {
        if (ev.action === "finalize") {
          // Finalize advanced the job to Mango Reviewing — it leaves the provider's
          // actionable queue, so return to the pipeline where they SEE it moved
          // (staying here would just show a stale FMV-Approval screen + Finalize btn).
          window.location.hash = "#/jobs";
        } else if (ev.action === "fmv-decline") {
          // Reprice / walk-away routed the job to Mango Reviewing (flagged for ops) —
          // same destination as finalize; it leaves the actionable queue. Close + return.
          this.closeDeclineModal();
          window.location.hash = "#/jobs";
        } else if (ev.action === "fmv") {
          // Replacement FMV uploaded — reload so the breakdown + net settlement refresh.
          this.setUploadStatus("FMV updated — recalculating settlement…", "ok");
          void this.load();
        }
      },
      onError: (ev, error) => {
        console.error(`[mango-finalize] ${ev.action} failed for ${ev.id}`, error);
        if (ev.action === "fmv-decline") this.setDeclineStatus("Could not submit. Please retry.", "err");
        else if (ev.action === "fmv") this.setUploadStatus("FMV upload failed. Please retry.", "err");
        else this.setStatus("Could not finalize this work order. Please retry.");
      },
    });

    this.wireDeclineModal();
    this.wireFmvUpload();
    await this.load();
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) void this.load();
  }

  private get shortName(): string {
    return this.getAttribute("data-short-name") ?? "";
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  // ── Replacement FMV upload (data-action="fmv") ──────────────────────────────
  /** Wire the "Upload updated FMV" picker → file input → enable the submit button.
   *  CSP-safe (no inline JS); the submit button fires data-action="fmv". */
  private wireFmvUpload(): void {
    const pick = this.querySelector<HTMLElement>("[data-fmv-pick]");
    const input = this.querySelector<HTMLInputElement>("[data-fmv-file]");
    const btn = this.querySelector<HTMLButtonElement>("[data-fmv-btn]");
    pick?.addEventListener("click", () => input?.click());
    input?.addEventListener("change", () => {
      if (btn) btn.disabled = !(input.files && input.files.length > 0);
      this.setUploadStatus("", "");
    });
  }

  /** Build a multipart form { file } from the FMV file input, or null. */
  private buildFmvForm(): FormData | null {
    const input = this.querySelector<HTMLInputElement>("[data-fmv-file]");
    const file = input?.files?.[0];
    if (!file) return null;
    const form = new FormData();
    form.append("file", file, file.name);
    return form;
  }

  private setUploadStatus(message: string, kind: "ok" | "err" | ""): void {
    const el = this.querySelector<HTMLElement>("[data-fmv-status]");
    if (!el) return;
    el.textContent = message;
    el.className = `upload-status${kind ? " " + kind : ""}`;
  }

  // ── Decline / reprice modal (Q3) ──────────────────────────────────────────
  /** Wire the open/close + the reprice⇔walk-away mutual exclusivity. CSP-safe
   *  (no inline JS); the Confirm button itself fires data-action="fmv-decline". */
  private wireDeclineModal(): void {
    const overlay = this.querySelector<HTMLElement>("[data-decline-modal]");
    const price = this.querySelector<HTMLInputElement>("[data-decline-price]");
    const walkaway = this.querySelector<HTMLInputElement>("[data-decline-walkaway]");

    this.querySelector<HTMLElement>("[data-decline-open]")?.addEventListener("click", () =>
      this.openDeclineModal(),
    );
    this.querySelector<HTMLElement>("[data-decline-cancel]")?.addEventListener("click", () =>
      this.closeDeclineModal(),
    );
    // Click the dim backdrop (but not the modal card) to dismiss.
    overlay?.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeDeclineModal();
    });
    // Walk away disables + clears the counter-price (the two outcomes are exclusive).
    walkaway?.addEventListener("change", () => {
      if (price) {
        price.disabled = walkaway.checked;
        if (walkaway.checked) price.value = "";
      }
      this.setDeclineStatus("", "");
    });
  }

  private openDeclineModal(): void {
    this.setDeclineStatus("", "");
    this.querySelector<HTMLElement>("[data-decline-modal]")?.classList.add("show");
  }

  private closeDeclineModal(): void {
    this.querySelector<HTMLElement>("[data-decline-modal]")?.classList.remove("show");
  }

  private setDeclineStatus(message: string, kind: "ok" | "err" | ""): void {
    const el = this.querySelector<HTMLElement>("[data-decline-status]");
    if (!el) return;
    el.textContent = message;
    el.className = `upload-status${kind ? " " + kind : ""}`;
  }

  /** Build the fmv-decline body from the modal, or false (with a status message)
   *  if the form is incomplete — mirrors the server validator (reason required;
   *  reprice needs a counter_price; walk-away takes none). */
  private buildDeclineBody(): FmvDeclineBody | false {
    const walkAway = this.querySelector<HTMLInputElement>("[data-decline-walkaway]")?.checked ?? false;
    const reason = (this.querySelector<HTMLTextAreaElement>("[data-decline-reason]")?.value ?? "").trim();
    const priceRaw = (this.querySelector<HTMLInputElement>("[data-decline-price]")?.value ?? "").trim();

    if (!reason) {
      this.setDeclineStatus("A reason is required.", "err");
      return false;
    }
    if (walkAway) {
      return { walk_away: true, reason };
    }
    const price = Number(priceRaw);
    if (!priceRaw || !Number.isFinite(price) || price <= 0) {
      this.setDeclineStatus("Enter a counter-price, or check “not interested in purchasing”.", "err");
      return false;
    }
    return { walk_away: false, counter_price: priceRaw, reason };
  }

  private async load(): Promise<void> {
    const id = this.shortName;
    if (!id) {
      this.setStatus("No work order specified.");
      return;
    }
    this.setStatus("Loading work order…");
    try {
      this.job = await getWorkOrder(id);
    } catch (error) {
      console.error("[mango-finalize] failed to load", error);
      this.setStatus("Could not load this work order.");
      return;
    }
    this.render();
  }

  private render(): void {
    const job = this.job;
    if (!job) return;
    applyBindings(this, { job });

    // Weights + device-count bindings (pass through; "—" when null).
    const passOrDash = (field: string, value: string | number | null | undefined): void => {
      this.querySelectorAll<HTMLElement>(`[data-bind="job.${field}"]`).forEach(
        (el) => (el.textContent = value == null ? "—" : String(value)),
      );
    };
    passOrDash("weight_total", job.weight_total);
    passOrDash("weight_ewaste", job.weight_ewaste);
    passOrDash("devices_wiped", job.devices_wiped);
    passOrDash("devices_shredded", job.devices_shredded);
    // Scope hours carry an "hrs" suffix when present.
    const hrs = (field: string, value: string | null | undefined): void => {
      this.querySelectorAll<HTMLElement>(`[data-bind="job.${field}"]`).forEach(
        (el) => (el.textContent = value == null ? "—" : `${value} hrs`),
      );
    };
    hrs("smarthands_hours", job.smarthands_hours);
    hrs("specialized_hours", job.specialized_hours);
    // pickup_region falls back to a dash when absent.
    passOrDash("pickup_region", job.pickup_region);

    // Scope-expansion notes (the Onsite-Job-Complete onsite_notes), operational —
    // BOTH roles. Show the callout only when the provider actually logged a note.
    const scopeNotes = (job.scope_notes ?? "").trim();
    setText(this, "[data-scope-notes-text]", scopeNotes);
    toggle(this, "[data-scope-notes]", scopeNotes.length > 0);

    // Date bindings.
    const fmtDate = (field: string, value: string | null): void => {
      this.querySelectorAll<HTMLElement>(`[data-bind="job.${field}"]`).forEach(
        (el) => (el.textContent = date(value)),
      );
    };
    fmtDate("dropoff_datetime", job.dropoff_datetime);
    fmtDate("accepted_at", job.accepted_at);
    fmtDate("onsite_completed_at", job.onsite_completed_at);
    fmtDate("sow_submitted_at", job.sow_submitted_at);
    fmtDate("mango_approved_at", job.mango_approved_at);

    // Documents verified (BOTH roles) — derived from deliverables + fmv_submitted_at.
    this.renderDocuments(job);

    // Timeline "days to complete" (BOTH roles).
    this.renderDaysToComplete(job);

    // Role gate (cosmetic): Admin sees the center column + finalize box; Staff the gate.
    const isAdmin = hasFinancials(job);
    toggle(this, "[data-fmv-center]", isAdmin);
    toggle(this, "[data-finalize-box]", isAdmin);
    toggle(this, "[data-staff-gate]", !isAdmin);

    if (isAdmin) {
      this.renderProvisional(job);
      this.renderBreakdown(job);
      this.renderNetSettlement(job);
    }

    this.setStatus("");
  }

  // ── Provisional-until-approved caveat (J14) ─────────────────────────────────
  // Jordan (2026-06-06): FMV1/FMV2 are the provider's ASSESSED estimates and are
  // not accurate/final until Mango approves the assessment. While mango_approved_at
  // is null the FMV figures are provisional (amber); once Mango approves they are
  // final (green). Gated on the timestamp's presence — there is no boolean flag.
  private renderProvisional(job: WorkOrder): void {
    const approved = !!job.mango_approved_at;
    const banner = this.querySelector<HTMLElement>("[data-fmv-provisional]");
    if (banner) banner.className = `banner ${approved ? "banner-green" : "banner-amber"}`;
    setText(
      this,
      "[data-fmv-provisional-text]",
      approved
        ? `Approved by Mango on ${date(job.mango_approved_at)} — these FMV figures are final.`
        : "Provisional — these are the provider's assessed estimates, pending Mango approval. " +
            "FMV figures are not final until the assessment is approved.",
    );
    setText(
      this,
      "[data-fmv-subtitle]",
      approved
        ? "Provider-assessed values · approved by Mango"
        : "Provider-assessed estimates · pending Mango approval",
    );
  }

  // ── Documents verified ──────────────────────────────────────────────────────
  /** Derive the three doc rows from `deliverables` + `fmv_submitted_at`. The
   *  contract has ONE `cod` deliverable (the prototype split wipe/shred) → ONE row. */
  private renderDocuments(job: WorkOrder): void {
    const byType = new Map<string, string>();
    for (const d of job.deliverables ?? []) byType.set(d.deliverable_type, d.status);

    const codDone = byType.get("cod") === "complete";
    const invDone = byType.get("asset_inventory") === "complete";
    const fmvDone = !!job.fmv_submitted_at;

    this.setDocRow("[data-doc-cod]", codDone, "Verified", "Pending");
    this.setDocRow("[data-doc-inventory]", invDone, "Verified", "Pending");
    this.setDocRow("[data-doc-fmv]", fmvDone, "Uploaded", "Pending");
  }

  /** Set a documents-verified row: a check + the done label when complete,
   *  else the muted pending label. */
  private setDocRow(selector: string, done: boolean, doneLabel: string, pendingLabel: string): void {
    const el = this.querySelector<HTMLElement>(selector);
    if (!el) return;
    el.textContent = "";
    if (done) {
      el.className = "row-value green";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.gap = "4px";
      const check = document.createElement("span");
      check.textContent = "✓";
      el.append(check, document.createTextNode(` ${doneLabel}`));
    } else {
      el.className = "row-value";
      el.style.display = "";
      el.style.color = "var(--text-3)";
      el.textContent = pendingLabel;
    }
  }

  // ── Timeline: days to complete ──────────────────────────────────────────────
  /** Calendar days from accepted_at to mango_approved_at (or now if not yet
   *  approved). Simple inclusive day count; "—" if not yet accepted. */
  private renderDaysToComplete(job: WorkOrder): void {
    const el = this.querySelector<HTMLElement>("[data-days-to-complete]");
    if (!el) return;
    if (!job.accepted_at) {
      el.textContent = "—";
      return;
    }
    const start = new Date(job.accepted_at).getTime();
    const end = job.mango_approved_at ? new Date(job.mango_approved_at).getTime() : Date.now();
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
      el.textContent = "—";
      return;
    }
    const days = Math.max(0, Math.round((end - start) / 86_400_000));
    const suffix = job.mango_approved_at ? "" : " (so far)";
    el.textContent = `${days} day${days === 1 ? "" : "s"}${suffix}`;
  }

  // ── FMV breakdown table (Admin only) ────────────────────────────────────────
  /** Display-case a raw FMV grade ("passed"/"minor"/"nonfunctional"/…) for the UI. */
  private conditionLabel(raw: string): string {
    const map: Record<string, string> = {
      passed: "Passed",
      minor: "Minor Issues",
      nonfunctional: "Non-Functional",
      failed: "Failed",
    };
    const key = (raw ?? "").toLowerCase();
    if (map[key]) return map[key];
    // Generic fallback: split on _/-/space, Title-Case each token.
    return (raw ?? "")
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ") || "—";
  }

  private renderBreakdown(job: WorkOrder): void {
    const tbody = this.querySelector<HTMLElement>("[data-fmv-rows]");
    if (tbody) {
      tbody.textContent = "";
      const rows = job.fmv_breakdown?.rows ?? [];
      for (const r of rows) {
        const tr = document.createElement("tr");

        const tdType = document.createElement("td");
        tdType.setAttribute("data-label", "Device type");
        const typeInner = document.createElement("div");
        typeInner.style.fontWeight = "600";
        typeInner.textContent = r.device_type;
        tdType.append(typeInner);

        const tdQty = document.createElement("td");
        tdQty.setAttribute("data-label", "Qty");
        tdQty.textContent = String(r.qty);

        const tdCond = document.createElement("td");
        tdCond.setAttribute("data-label", "Condition");
        tdCond.textContent = this.conditionLabel(r.condition);

        const tdUnit = document.createElement("td");
        tdUnit.setAttribute("data-label", "FMV / unit");
        tdUnit.style.fontFamily = "var(--mono)";
        tdUnit.textContent = `$${money(r.fmv_unit)}`;

        const tdTotal = document.createElement("td");
        tdTotal.setAttribute("data-label", "Total FMV");
        tdTotal.textContent = `$${money(r.total_fmv)}`;

        tr.append(tdType, tdQty, tdCond, tdUnit, tdTotal);
        tbody.append(tr);
      }
    }
    setText(this, "[data-fmv-total]", `$${money(job.fmv_breakdown?.total_assessed_fmv)}`);
  }

  // ── Net settlement (Admin only) ─────────────────────────────────────────────
  // The settlement is now simply: Equipment purchase (FMV-2) − Scope/overage comp
  // = Net owed to Mango. The old per-line client-overage subtractions (wipe /
  // e-waste / flat fee) are gone — those bill the CLIENT, not the provider —
  // along with `scope_expansion_credit` (replaced by `provider_overage_comp`, the
  // above-ceiling vendor comp that nets against the purchase).
  private renderNetSettlement(job: WorkOrder): void {
    const ns = job.net_settlement;
    if (!ns) return;
    setText(this, "[data-net-equipment]", `$${money(ns.equipment_purchase_price)}`);
    setText(this, "[data-net-comp]", `−$${money(ns.provider_overage_comp)}`);

    // net_amount_due = equipment_purchase_price − provider_overage_comp. It is no
    // longer signed/negative for normal jobs (with no above-ceiling work it equals
    // the equipment purchase price). Keep a defensive negative guard — it won't
    // trigger under the new model but harms nothing if a future job goes negative.
    const net = Number(ns.net_amount_due);
    const negative = Number.isFinite(net) && net < 0;
    const absStr = negative ? ns.net_amount_due.replace(/^-/, "") : ns.net_amount_due;
    setText(this, "[data-net-label]", negative ? "Net credit to you" : "Net amount due to Mango");
    setText(this, "[data-net-amount]", `$${money(absStr)}`);

    // Finalize-box mini summary.
    setText(this, "[data-fin-total-fmv]", `$${money(job.fmv_breakdown?.total_assessed_fmv)}`);
    setText(this, "[data-fin-comp]", `−$${money(ns.provider_overage_comp)}`);
    setText(this, "[data-fin-net-label]", negative ? "Net credit to you" : "Net amount due to Mango");
    setText(this, "[data-fin-net-amount]", `$${money(absStr)}`);
  }
}

export function registerMangoFinalize(): void {
  if (!customElements.get("mango-finalize")) {
    customElements.define("mango-finalize", MangoFinalize);
  }
}
